import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Panel, PanelGroup } from 'react-resizable-panels';
import styles from './styles.module.css';
import ResizeHandle from './ResizeHandle';
import EventBus from '../common/EventBus';
import '../services/isMobile.js';
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';

import AuthService from "../services/auth.service";
import TagsTrackingMap from './tagtracking/TagsTrackingMap.js';
import BackendTagsService from '../services/backendTags.service.js';
import TagsTrackingGrid from './tagtracking/TagsTrackingGrid.js';
import { customSelectTheme } from '../common/CustomSelectTheme.js';

// Options for history duration
const historyOptions = [
  { value: '1h', label: '1 Hour' },
  { value: '3h', label: '3 Hours' },
  { value: '6h', label: '6 Hours' },
  { value: '12h', label: '12 Hours' },
  { value: '24h', label: '1 Day' },
  { value: '2d', label: '2 Days' },
  { value: '3d', label: '3 Days' },
  { value: '4d', label: '4 Days' },
  { value: '5d', label: '5 Days' },
  { value: '6d', label: '6 Days' },
  { value: '7d', label: '7 Days' }
];

const BoardTagsTracking = React.memo((props) => {
  const [startMarkerCoordinates, setStartMarkerCoordinates] = useState([0, 0]);
  const [endMarkerCoordinates, setEndMarkerCoordinates] = useState([0, 0]);

  // Load history duration from localStorage or default to '3d'
  const initialHistory = localStorage.getItem('historyDuration') || "3d";
  const [historyDuration, setHistoryDuration] = useState(initialHistory);

  const [contentData, setContentData] = useState([]);
  const [filteredContentData, setFilteredContentData] = useState([]);
  const [contentRunSelected, setContentRunSelected] = useState(null);

  const mapRef = useRef(null);

  const currentUser = AuthService.getCurrentUser();
  const isAdmin = currentUser.roles.includes("admin_smart");
  const isPartner = currentUser.roles.includes("partner");

  const key = 'L67Uis9Da5SZCWwoyVMF';

  const mapStyleStreetLight = `https://api.maptiler.com/maps/streets-v2/style.json?key=${key}`;
  const mapStyleSatLight = `https://api.maptiler.com/maps/hybrid/style.json?key=${key}`;
  const MAPBOX_ACCESS_TOKEN = 'sk.eyJ1Ijoia294eDMiLCJhIjoiY2xidGU1ZWF5MDI5azN3bXNlNmRoYTJhZSJ9.cHWjuYbOwKb_T4wITm65ZA';

  const [mapStyle, setMapStyle] = useState(
    localStorage.getItem('tagsTrackingMapStyle') || mapStyleStreetLight
  );

  const isDesktop = !window.mobileCheck();

  let toastId = [0];

  // Handle history duration change and persist to localStorage
  const handleHistoryDurationChange = (selectedOption) => {
    setHistoryDuration(selectedOption.value);
    localStorage.setItem('historyDuration', selectedOption.value); // Save selected duration to localStorage
  };

  // Filter content data based on selected history duration
  const filterDataByDuration = () => {
    const now = new Date();
    let cutoffTime;

    // Calculate cutoff time for durations less than 1 day
    switch (historyDuration) {
      case '1h':
        cutoffTime = now.getTime() - 1 * 60 * 60 * 1000;
        break;
      case '3h':
        cutoffTime = now.getTime() - 3 * 60 * 60 * 1000;
        break;
      case '6h':
        cutoffTime = now.getTime() - 6 * 60 * 60 * 1000;
        break;
      case '12h':
        cutoffTime = now.getTime() - 12 * 60 * 60 * 1000;
        break;
      case '24h':
        cutoffTime = now.getTime() - 24 * 60 * 60 * 1000;
        break;
      default:
        // Handle durations of 1 day or more (use API data directly)
        setFilteredContentData(contentData);
        return;
    }

    // Filter the data based on cutoff time
    const filteredData = contentData.filter(item => {
      const itemTimestamp = new Date(item.timestamp * 1000).getTime();
      return itemTimestamp >= cutoffTime;
    });

    if (filteredData.length === 0) {
      toast.warning("No data on this time interval", {
        position: "bottom-right",
        autoClose: 5000
      });
    }

    setFilteredContentData(filteredData);
  };

  // Run the filter whenever the history duration or contentData changes
  useEffect(() => {
    if (historyDuration !== "1d" && contentData.length > 0) {
      filterDataByDuration();
    } else {
      setFilteredContentData(contentData); // No filtering needed for 1 day or more
    }
  }, [historyDuration, contentData]);

  const toggleMapStyleClick = () => {
    if (mapStyle === mapStyleStreetLight) {
      setMapStyle(mapStyleSatLight);
      localStorage.setItem('tagsTrackingMapStyle', mapStyleSatLight);
    } else {
      setMapStyle(mapStyleStreetLight);
      localStorage.setItem('tagsTrackingMapStyle', mapStyleStreetLight);
    }
  };

  const resizeMap = () => {
    if (mapRef?.current) {
      console.log("BoardTagsTracking / resizeMap / mapRef.current", mapRef.current); // Added log
      const map = mapRef.current.getMap();
      console.log("BoardTagsTracking / resizeMap / map instance", map); // Added log
      if (map) {
        map.resize();
      } else {
        console.warn("BoardTagsTracking / resizeMap: Map instance is undefined."); // Added log
      }
    } else {
      console.warn("BoardTagsTracking / resizeMap: mapRef.current is undefined."); // Added log
    }
  };

  const handleSelectDevice = useCallback((row) => {
    // let toastIdTmp = toast.loading('Loading data...', {
    //   position: "bottom-right",
    //   autoClose: false,
    // });
    // toastId.push(toastIdTmp);

    setContentRunSelected(row);

    let nbDays = 1;

    // Determine how many days to fetch based on the selected history duration
    if (historyDuration.endsWith('d')) {
      nbDays = parseInt(historyDuration); // Fetch exact days (1d, 2d, etc.)
    }

    BackendTagsService.getDeviceTagLocations(row.tag_keys, nbDays).then(
      (response) => {
        if (response) {
          if (response?.found_keys?.length > 0) {
            response.reports = response.reports.reverse();
            setContentData(response.reports);
            setStartMarkerCoordinates([response.reports[0].lon, response.reports[0].lat]);
            setEndMarkerCoordinates([response.reports[response.reports.length - 1].lon, response.reports[response.reports.length - 1].lat]);

            // toastId.forEach((id) => {
            //   console.log("remove toast 1 :", id);
            //   toast.dismiss(id);
            // });
          } else {
            //toastId.forEach((id) => {
            //  console.log("remove toast 2 :", id);
            //  toast.dismiss(id);
            //});

            // toast.warning("No data", {
            //   position: "bottom-right",
            //   autoClose: 5000
            // });

            setFilteredContentData([]);
          }
        } else {
          // toastId.forEach((id) => {
          //   console.log("remove toast 3 :", id);
          //   toast.dismiss(id);
          // });

          toast.error("Fail to load data", {
            position: "bottom-right",
            autoClose: 5000
          });

          setFilteredContentData([]);
        }
      }
    );
  }, [historyDuration]);

  return (
    <>
      <ToastContainer />

      {isDesktop && (
        <div className={styles.Container} style={{ height: '92vh', flex: '1' }}>
          <PanelGroup direction='horizontal'>
            <Panel collapsible={true} defaultSize={20} order={1} onResize={resizeMap}>
              <PanelGroup direction='vertical'>
                <Panel className={styles.Panel} collapsible={true}>
                  <TagsTrackingGrid onSelectDevice={handleSelectDevice} />
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                      className='btn m-2 btn-outline-primary'
                      style={{ flex: 1 }}
                      onClick={toggleMapStyleClick}
                    >
                      Street / Sat
                    </button>
                    <Select
                      className="m-1"
                      options={historyOptions}
                      value={historyOptions.find(option => option.value === historyDuration)} // Set selected option
                      onChange={handleHistoryDurationChange}
                      theme={customSelectTheme}
                      menuPlacement="top"
                    />
                  </div>
                </Panel>
              </PanelGroup>
            </Panel>
            <ResizeHandle iconName={'fa fa-left-right'} />
            <Panel collapsible={false} order={2}>
              <PanelGroup direction='vertical'>
                <Panel className={styles.Panel} collapsible={true} defaultSize={70} minSize={40} order={1}>
                  <div style={{ height: '50vh', flex: '1' }} className='map-container'>
                    <TagsTrackingMap
                      startMarkerCoordinates={startMarkerCoordinates}
                      endMarkerCoordinates={endMarkerCoordinates}
                      contentData={filteredContentData} // Pass the filtered data
                      contentRunSelected={contentRunSelected}
                      showHistory={true}
                      mapStyle={mapStyle}
                      MAPBOX_ACCESS_TOKEN={MAPBOX_ACCESS_TOKEN}
                      mapRef={mapRef}
                      isDesktop={isDesktop}
                    />
                  </div>
                </Panel>
              </PanelGroup>
            </Panel>
          </PanelGroup>
        </div>
      )}
      {!isDesktop && (
        <>
          <div style={{ flex: '1' }}>
            <div style={{ height: '40vh', overflow: 'auto' }}>
              <TagsTrackingGrid onSelectDevice={handleSelectDevice} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                className='btn m-2 btn-outline-primary'
                style={{ flex: 1 }}
                onClick={toggleMapStyleClick}
              >
                Street / Sat
              </button>
              <div style={{ paddingTop: '4px' }}>
                <Select
                  className="m-1"
                  options={historyOptions}
                  value={historyOptions.find(option => option.value === historyDuration)} // Set selected option
                  onChange={handleHistoryDurationChange}
                  theme={customSelectTheme}
                  menuPlacement="top"
                />
              </div>
            </div>
            <div style={{ height: '50vh', flex: '1' }} className='map-container'>
              <TagsTrackingMap
                startMarkerCoordinates={startMarkerCoordinates}
                endMarkerCoordinates={endMarkerCoordinates}
                contentData={filteredContentData} // Pass the filtered data
                contentRunSelected={contentRunSelected}
                showHistory={true}
                mapStyle={mapStyle}
                MAPBOX_ACCESS_TOKEN={MAPBOX_ACCESS_TOKEN}
                mapRef={mapRef}
                isDesktop={isDesktop}
              />
            </div>
            <div style={{ height: '2vh' }} />
          </div>
        </>
      )}
    </>
  );
});


export default React.memo(BoardTagsTracking);
